import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Clock, MapPin, Download, ArrowUpRight, ArrowLeft, Check, Quote, X, Building2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PROJECTS, PROJECT_DETAILS, img } from "@/config";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [lightbox, setLightbox] = useState(null);

  const project = PROJECTS.find((p) => p.slug === slug);
  const detail = PROJECT_DETAILS[slug];

  if (!project || !detail) return <Navigate to="/projects" replace />;

  const related = PROJECTS.filter((p) => p.slug !== slug).slice(0, 3);
  const pdfHref = `/case-studies/${slug}.pdf`;

  return (
    <div data-testid="project-detail-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-16 lg:pb-20">
        <div className="absolute inset-0 -z-10">
          <img src={img(project.image)} alt={project.title} className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1120]/85 via-[#0B1120]/90 to-[#0B1120]" />
          <div className="tech-grid absolute inset-0 opacity-50" />
        </div>
        <div className="glow-blue pointer-events-none absolute -left-20 top-10 h-[400px] w-[400px]" />

        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-2 text-sm font-medium text-slate-400 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All projects
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-[#2563EB] px-3 py-1 text-xs font-semibold text-white">{project.category}</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#60A5FA]"><Camera className="h-4 w-4" /> {project.metric}</span>
            <span className="inline-flex items-center gap-1.5 text-sm text-slate-400"><Clock className="h-4 w-4" /> {project.duration}</span>
          </div>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white lg:text-6xl">{project.title}</h1>
          <p className="mt-4 inline-flex items-center gap-2 text-slate-400"><Building2 className="h-4 w-4 text-[#2563EB]" /> Client: <span className="font-medium text-white">{project.client}</span></p>
        </div>
      </section>

      {/* Overview + results */}
      <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-2xl font-bold text-white">Overview</h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-300">{detail.overview}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="grid grid-cols-3 gap-4">
              {detail.results.map((r) => (
                <div key={r.label} className="rounded-2xl border border-slate-800 bg-[#111c33]/40 p-5 text-center">
                  <p className="font-display text-2xl font-extrabold tracking-tight text-[#60A5FA] lg:text-3xl">{r.value}</p>
                  <p className="mt-1 text-xs text-slate-400">{r.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Challenge + Solution */}
      <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-10">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-slate-800 bg-[#111c33]/40 p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">The challenge</p>
              <p className="mt-4 text-lg leading-relaxed text-slate-300">{detail.challenge}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-slate-800 bg-[#111c33]/40 p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Our solution</p>
              <ul className="mt-5 space-y-3">
                {detail.solution.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2563EB]/20 text-[#60A5FA]"><Check className="h-3 w-3" /></span>
                    <span className="text-sm leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        {/* services chips */}
        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-500">Services delivered:</span>
            {detail.services.map((s) => (
              <span key={s} className="rounded-full border border-slate-800 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-300">{s}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Gallery */}
      <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-10" data-testid="project-gallery">
        <Reveal><h2 className="font-display text-2xl font-bold text-white">Project gallery</h2></Reveal>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {detail.gallery.map((g, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <button onClick={() => setLightbox(img(g))} data-testid={`gallery-image-${i}`} className="group block aspect-[4/3] w-full overflow-hidden rounded-2xl border border-slate-800">
                <img src={img(g)} alt={`${project.title} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Testimonial */}
      {detail.testimonial && (
        <section className="mx-auto max-w-4xl px-6 pb-16 lg:px-10">
          <Reveal>
            <figure className="rounded-3xl border border-slate-800 bg-[#111c33]/40 p-8 text-center lg:p-12">
              <Quote className="mx-auto h-10 w-10 text-[#2563EB]/40" />
              <blockquote className="mt-6 font-display text-xl font-medium leading-relaxed text-white lg:text-2xl">"{detail.testimonial.quote}"</blockquote>
              <figcaption className="mt-6 text-sm font-semibold text-[#60A5FA]">— {detail.testimonial.author}</figcaption>
            </figure>
          </Reveal>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-10">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 rounded-[2rem] border border-slate-800 bg-[#111c33] p-8 lg:flex-row lg:items-center lg:p-12">
            <div className="flex items-center gap-4">
              <MapPin className="h-6 w-6 text-[#2563EB]" />
              <h2 className="max-w-lg font-display text-2xl font-extrabold tracking-tight text-white lg:text-3xl">Planning a similar deployment?</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={pdfHref} download data-testid="download-case-study-pdf" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/5"><Download className="h-4 w-4" /> Download case study</a>
              <Link to="/contact" data-testid="project-detail-cta" className="group inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#3b82f6]">Get a Free Site Survey <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Related */}
      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10">
        <h2 className="mb-8 font-display text-2xl font-bold text-white">More projects</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {related.map((p) => (
            <Link key={p.slug} to={`/projects/${p.slug}`} data-testid={`related-project-${p.slug}`} className="group relative block overflow-hidden rounded-3xl border border-slate-800">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={img(p.image)} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <span className="rounded-full bg-[#2563EB] px-2.5 py-0.5 text-xs font-semibold text-white">{p.category}</span>
                <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-white">{p.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Gallery lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLightbox(null)} data-testid="gallery-lightbox" className="fixed inset-0 z-[70] flex items-center justify-center bg-[#020617]/85 p-4 backdrop-blur-sm">
            <button data-testid="gallery-lightbox-close" className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-[#0B1120]/70 text-white" aria-label="Close"><X className="h-5 w-5" /></button>
            <motion.img initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} src={lightbox} alt="" className="max-h-[85vh] max-w-5xl rounded-2xl border border-slate-700 object-contain" onClick={(e) => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
